import { Model, ModelData } from '../general'

import Product, {
  ConnectivityState as ProductConnectivityState,
  OccupancyState as ProductOccupancyState,
  PeripheralsState as ProductPeripheralsState,
  Data as ProductData,
} from './product'

import { VisualProgram, Weekday } from './schedule'

export interface Schedule {
  id: number
  visual_program: Record<Weekday, VisualProgram>
}

export interface FloorplanPlacementData {
  element_id?: number
  position?: {
    x: number
    y: number
    rotation: number
  }
}

export enum ScheduleState {
  None = 0,
  // Blocked = 1,
  Cleaning = 2,
}

export enum TriggerState {
  None = 0,
  Blue = 1,
  Red = 2,
  Orange = 3,
  Yellow = 4,
  Purple = 5,
  Green = 6,
}

export interface Data extends ModelData {
  connectivity_state?: number
  floor_id?: number
  floorplan?: FloorplanPlacementData
  has_floorplan?: boolean
  location_id?: number
  installed_devices_count?: number
  name?: string
  comments_count?: number
  photos_count?: number
  occupancy_state?: number
  peripherals_state?: number
  schedule_state?: number
  trigger_state?: number
  product?: Omit<ProductData, 'company_id'>
  vendor?: string
  workspace_type_id?: number
}

class Workspace extends Model<Data> {
  get name() {
    return this.data.name
  }

  get vendor() {
    return this.data.vendor
  }

  get workspaceTypeId() {
    return this.data.workspace_type_id
  }

  get floorId() {
    return this.data.floor_id
  }

  get searchText(): string {
    return `${this.name}`
  }

  get numberOfComments(): number {
    return this.data.comments_count || 0
  }
  get numberOfPhotos(): number {
    return this.data.photos_count || 0
  }
  get numberOfRemarks() {
    return this.numberOfComments + this.numberOfPhotos
  }

  get connectivityState() {
    switch (this.data.connectivity_state) {
      case 0:
        return ProductConnectivityState.Connected
      case 1:
        return ProductConnectivityState.ConnectivityLost
      case 2:
        return ProductConnectivityState.Disconnected
      case 3:
        return ProductConnectivityState.NeverOnline
      default:
        return undefined
    }
  }

  get peripheralsState() {
    switch (this.data.peripherals_state) {
      case 0:
        return ProductPeripheralsState.Connected
      case 1:
        return ProductPeripheralsState.SensorDisconnected
      case 2:
        return ProductPeripheralsState.LedDisconnected
      case 3:
        return ProductPeripheralsState.SensorAndLedDisconnected
      case 4:
        return ProductPeripheralsState.Unknown
      default:
        return undefined
    }
  }

  get occupancyState() {
    switch (this.data.occupancy_state) {
      case 0:
        return ProductOccupancyState.Unknown
      case 1:
        return ProductOccupancyState.Free
      case 2:
        return ProductOccupancyState.Occupied
      default:
        return undefined
    }
  }

  get scheduleState() {
    switch (this.data.schedule_state) {
      case 0:
        return ScheduleState.None
      // case 1:
      //   return ScheduleState.Blocked
      case 2:
        return ScheduleState.Cleaning
      default:
        return undefined
    }
  }

  get triggerState() {
    switch (this.data.trigger_state) {
      case 0:
        return TriggerState.None
      case 1:
        return TriggerState.Blue
      case 2:
        return TriggerState.Red
      case 3:
        return TriggerState.Orange
      case 4:
        return TriggerState.Yellow
      case 5:
        return TriggerState.Purple
      case 6:
        return TriggerState.Green
      default:
        return undefined
    }
  }
  get floorplanElementId() {
    return this.data.floorplan?.element_id
  }
  get floorplanPosition() {
    return this.data.floorplan?.position
  }
  get floorplanIsPlaced() {
    const position = this.data.floorplan?.position
    if (!position) return false
    const { x, y, rotation } = position
    return Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(rotation)
  }

  get product() {
    if (this.data.product?.id) return new Product(this.parent, this.data.product)
    return undefined
  }
}

export default Workspace
